import { ICellRendererParams } from 'ag-grid-community';
import { Typography } from '@navi/web-ui/lib/primitives';
import { formatAmount } from '@cp/src/utils/commonUtils';
import { GLOBAL } from '@cp/src/constants/Global';
import DateTime from '../../DateTime';
import dayjs from 'dayjs';
import { ColDefsType } from '@navi/web-ui/lib/components/AgTable/types';
import AddNumberPopper from './AddNumberPopper';
import { IAnnotatedSpan } from '../type';

const AGENT_LAPTOP_BORDER_WIDTH = 1400;
const DEFAULT_WIDTH = 200;
const WIDTHS = {
  DATE_WIDTH: 130,
  TRANSACTION_NARRATION_WIDTH: 330,
  AMOUNT_WIDTH: 120,
  BALANCE_WIDTH: 120
};

const columnWidth = (percentage: number) => {
  const parent = document.getElementById('bankStatementTable') as HTMLElement;
  if (parent && parent?.offsetWidth) {
    return (parent.offsetWidth * percentage) / 100;
  }
  return DEFAULT_WIDTH;
};

const attributesWidth = {
  dateWidth: (percentage: number) =>
    GLOBAL.SCREEN_WIDTH < AGENT_LAPTOP_BORDER_WIDTH ? WIDTHS.DATE_WIDTH : columnWidth(percentage),
  transactionNarrationWidth: (percentage: number) =>
    GLOBAL.SCREEN_WIDTH < AGENT_LAPTOP_BORDER_WIDTH
      ? WIDTHS.TRANSACTION_NARRATION_WIDTH
      : columnWidth(percentage),
  amountWidth: (percentage: number) =>
    GLOBAL.SCREEN_WIDTH < AGENT_LAPTOP_BORDER_WIDTH ? WIDTHS.AMOUNT_WIDTH : columnWidth(percentage),
  balanceWidth: (percentage: number) =>
    GLOBAL.SCREEN_WIDTH < AGENT_LAPTOP_BORDER_WIDTH ? WIDTHS.BALANCE_WIDTH : columnWidth(percentage)
};

export const getBankStatementInfo: ColDefsType[] = [
  {
    field: 'transactionDateTime',
    headerName: 'Date & Time',
    width: attributesWidth.dateWidth(15),
    cellStyle: {
      paddingRight: '0',
      cursor: 'auto'
    },

    cellRenderer: (params: ICellRendererParams) => {
      const { data } = params;
      return <DateTime istDate={dayjs(Number(data?.transactionDateTime)).toDate()} />;
    }
  },
  {
    field: 'transactionNarration',
    headerName: 'Transaction narration',
    width: attributesWidth.transactionNarrationWidth(54.9),
    autoHeight: true,
    cellStyle: {
      paddingRight: '0',
      cursor: 'auto',
      wordBreak: 'break-word'
    },

    cellRenderer: (params: ICellRendererParams) => {
      const { data } = params;
      const narration = data?.transactionNarration?.narration || '';
      const annotatedSpans = Array.isArray(data?.transactionNarration?.filterStart)
        ? data.transactionNarration.filterStart
        : [];
      const searchStart = Array.isArray(data?.transactionNarration?.searchStart)
        ? data.transactionNarration.searchStart
        : [];
      const searchValue = data?.transactionNarration?.searchValue || '';

      const combinedAnnotations = [
        ...annotatedSpans.map((span: IAnnotatedSpan) => ({
          start: span.start,
          end: span.end,
          type: 'annotated'
        })),
        ...searchStart.map((start: number) => ({
          start,
          end: start + searchValue.length,
          type: 'search'
        }))
      ].sort((a, b) => a.start - b.start);

      const mergedIntervals = [];
      if (combinedAnnotations.length > 0) {
        let current = { ...combinedAnnotations[0] };

        for (let i = 1; i < combinedAnnotations.length; i++) {
          const interval = combinedAnnotations[i];

          if (interval.start < current.end) {
            if (current.type === 'annotated' && interval.type === 'search') {
              if (interval.end > current.end) {
                mergedIntervals.push({ ...current, end: interval.start });
                mergedIntervals.push({
                  ...current,
                  start: interval.start,
                  end: current.end,
                  highlight: true
                });
                current = { ...interval, start: current.end };
              } else {
                current.highlight = true;
              }
            } else if (current.type === 'search' && interval.type === 'annotated') {
              mergedIntervals.push({ ...current, end: interval.start });
              current = { ...interval, highlight: true };
            } else {
              current.end = Math.max(current.end, interval.end);
            }
          } else {
            mergedIntervals.push(current);
            current = { ...interval };
          }
        }
        mergedIntervals.push(current);
      }

      const parts: any = [];
      let lastIndex = 0;
      if (mergedIntervals.length === 0) {
        parts.push(<span key={`text-${lastIndex}-${narration.length}`}>{narration}</span>);
      } else {
        mergedIntervals.forEach(annotation => {
          const { start, end, type, highlight } = annotation;

          if (lastIndex < start) {
            parts.push(
              <span key={`text-${lastIndex}-${start}`}>
                {narration.substring(lastIndex, start)}
              </span>
            );
          }

          const text = narration.substring(start, end);
          if (type === 'annotated') {
            if (highlight) {
              parts.push(
                <AddNumberPopper
                  key={`popper-${start}-${end}`}
                  data={{
                    ...data.transactionNarration,
                    start,
                    end
                  }}
                >
                  <span className="bg-yellow-50 font-bold">{text}</span>
                </AddNumberPopper>
              );
            } else {
              parts.push(
                <AddNumberPopper
                  key={`popper-${start}-${end}`}
                  data={{
                    ...data.transactionNarration,
                    start,
                    end
                  }}
                >
                  {text}
                </AddNumberPopper>
              );
            }
          } else if (type === 'search') {
            parts.push(
              <span key={`highlight-${start}-${end}`} className="bg-yellow-50">
                {text}
              </span>
            );
          }
          lastIndex = end;
        });

        if (lastIndex < narration.length) {
          parts.push(
            <span key={`text-${lastIndex}-${narration.length}`}>
              {narration.substring(lastIndex)}
            </span>
          );
        }
      }
      return <div>{parts}</div>;
    }
  },
  {
    field: 'transactionAmount',
    headerName: 'Amount',
    width: attributesWidth.amountWidth(15),
    cellStyle: {
      paddingRight: '0',
      cursor: 'auto'
    },
    cellRenderer: (params: ICellRendererParams) => {
      const { data } = params;
      const isCredit = data?.transactionType === 'CREDIT';
      const textColor = isCredit ? 'var(--navi-color-green-base)' : 'var(--navi-color-red-base)';
      const amountPrefix = isCredit ? '+' : '-';
      return (
        <Typography color={textColor} variant="p3">
          {`${amountPrefix} ${formatAmount(data?.transactionAmount)}`}
        </Typography>
      );
    }
  },
  {
    field: 'accountBalance',
    headerName: 'Balance',
    width: attributesWidth.balanceWidth(15),
    cellStyle: {
      paddingRight: '0',
      cursor: 'auto'
    },
    cellRenderer: (params: ICellRendererParams) => {
      const { data } = params;
      return (
        <Typography color={'var(--navi-color-gray-c2)'} variant="p3">
          {formatAmount(data?.accountBalance)}
        </Typography>
      );
    }
  }
];
